import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  Section,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  createPaybillEndpoint,
  paybillEndpointsQuery,
  paybillKeys,
  revokePaybillEndpoint,
} from '../api/paybill.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './billing.module.css';

/**
 * Settings > Billing > Paybill notifications (ADR 0020). Each address carries a secret, shown
 * once when it is made, for registering the paybill's confirmation address with M-Pesa.
 * TODO(verify-spec): the Daraja registration step itself.
 */
export function PaybillAddresses() {
  const online = useOnline();
  const queryClient = useQueryClient();
  const list = useQuery(paybillEndpointsQuery);
  const [label, setLabel] = useState('');
  const [shown, setShown] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => createPaybillEndpoint(label.trim()),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: paybillKeys.endpoints });
      setLabel('');
      setShown(`${window.location.origin}${result.confirmationPath}`);
      toasts.show({ title: 'Paybill address made' });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => revokePaybillEndpoint(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: paybillKeys.endpoints });
      toasts.show({ title: 'Paybill address switched off' });
    },
  });
  return (
    <Section
      title="Paybill notifications"
      description="Where M-Pesa tells Wodi about payments made straight to this facility's paybill or till."
    >
      {list.isPending ? <LoadingBlock label="Loading paybill addresses" rows={2} /> : null}
      {list.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void list.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(list.error, "Paybill addresses couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {list.isSuccess && list.data.mode === 'fake' ? (
        <Notice tone="info">
          Test connection (synthetic data). This server reads test notifications only.
        </Notice>
      ) : null}
      {list.isSuccess && !list.data.connected ? (
        <Notice tone="info">
          Paybill notifications are not connected on this server yet. Payments can still be recorded
          at the cashier by their M-Pesa code.
        </Notice>
      ) : null}
      {list.isSuccess && list.data.items.length === 0 ? <p>No paybill address yet.</p> : null}
      {list.isSuccess
        ? list.data.items.map((e) => (
            <p key={e.id} className={styles.toolbar}>
              <strong>{e.label}</strong>
              <Tag state={e.status === 'active' ? 'active' : 'neutral'}>
                {e.status === 'active' ? 'Active' : 'Switched off'}
              </Tag>
              <span className={styles.meta}>
                {e.status === 'active'
                  ? `Made ${formatInstant(e.createdAt)}`
                  : `Switched off ${formatInstant(e.revokedAt ?? e.createdAt)}`}
              </span>
              {e.status === 'active' ? (
                <Button
                  dense
                  variant="quiet"
                  isDisabled={!online}
                  isPending={revoke.isPending && revoke.variables === e.id}
                  pendingLabel="Switching off"
                  onPress={() => {
                    revoke.mutate(e.id);
                  }}
                >
                  Switch off
                </Button>
              ) : null}
            </p>
          ))
        : null}
      {revoke.isError ? (
        <Notice tone="error">{errorText(revoke.error, "The address wasn't switched off.")}</Notice>
      ) : null}
      <form
        className={styles.row}
        onSubmit={(event) => {
          event.preventDefault();
          if (label.trim() !== '') create.mutate();
        }}
      >
        <TextField
          label="Name for a new paybill address"
          description="For example Main paybill or Pharmacy till."
          value={label}
          onChange={setLabel}
          width="md"
        />
        <Button
          type="submit"
          isPending={create.isPending}
          pendingLabel="Making"
          isDisabled={!online}
        >
          Make address
        </Button>
      </form>
      {create.isError ? (
        <Notice tone="error">
          {errorText(create.error, "The address wasn't made. Try again.")}
        </Notice>
      ) : null}
      {shown === null ? null : (
        <Dialog
          title="Paybill address"
          isOpen
          onOpenChange={(open) => {
            if (!open) setShown(null);
          }}
        >
          <p>
            Give this address to M-Pesa as the paybill&apos;s confirmation address. It is shown only
            now. If it is lost, switch it off and make another.
          </p>
          <p className={`wodi-id ${styles.receiptPreview}`}>{shown}</p>
          <DialogActions>
            <Button
              variant="primary"
              onPress={() => {
                setShown(null);
              }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Section>
  );
}
